import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/lib/api'
import { Modal } from '@/shared/components/Modal'
import { AllowedRefsField } from '@/features/org/components/AllowedRefsField'

export function AddVcsTrustModal({ orgId, onClose, onAdded }: { orgId: string; onClose: () => void; onAdded: () => void }) {
  const [provider, setProvider] = useState<'github' | 'gitlab'>('github')
  const [subject, setSubject]   = useState('')
  // Mirrors the API default: omitted => ["main"]. Kept explicit here so the user sees it.
  const [allowedRefs, setAllowedRefs] = useState<string[] | null>(['main'])
  const [busy, setBusy]         = useState(false)
  const [error, setError]       = useState<string | null>(null)

  const placeholder = provider === 'github'
    ? 'repo:owner/repo,  repo:owner/*,  or  repo:owner/*-suffix'
    : 'project_path:group/project,  project_path:group/*,  or  project_path:group/*-suffix'

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (allowedRefs !== null && allowedRefs.length === 0) {
      setError('List at least one ref, or tick "Allow pushes from any ref".')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.post(`/api/orgs/${orgId}/vcs-trust`, { provider, subject, allowed_refs: allowedRefs })
      onAdded()
      onClose()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      track="org_trust_repo"
      title="Add Pipeline Trust"
      kicker="// oidc keyless auth"
      onClose={onClose}
      dismissable={!busy}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
          <button form="add-vcs-trust-form" type="submit" className="btn btn-primary btn-sm" disabled={busy}>
            {busy ? <span className="spinner" /> : 'Add'}
          </button>
        </div>
      }
    >
      <form id="add-vcs-trust-form" onSubmit={(e) => { void handleSubmit(e) }}>
        <p style={{ fontSize: '0.82rem', color: 'var(--text2)', marginBottom: 16, marginTop: 0 }}>
          Trusted repos authenticate using native OIDC tokens from GitHub Actions or GitLab CI - no stored secrets needed.
          See the <Link to="/integrations/oidc-trust" style={{ color: 'var(--accent)' }} onClick={onClose}>Pipeline Trust guide</Link> for details.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text2)', marginBottom: 4 }}>Provider</label>
            <select
              className="input"
              style={{ width: '100%', cursor: 'pointer' }}
              value={provider}
              onChange={(e) => setProvider(e.target.value as 'github' | 'gitlab')}
            >
              <option value="github">GitHub</option>
              <option value="gitlab">GitLab</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text2)', marginBottom: 4 }}>Subject</label>
            <input
              className="input"
              style={{ width: '100%' }}
              placeholder={placeholder}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              autoFocus
            />
            <p style={{ fontSize: '0.72rem', color: 'var(--text2)', marginTop: 6, marginBottom: 0 }}>
              Use <code>*</code> as a wildcard within a single path segment, e.g.{' '}
              <code>{provider === 'github' ? 'repo:acme/*-prod' : 'project_path:acme/*-prod'}</code>{' '}
              matches every repo ending in <code>-prod</code>. <code>*</code> never spans{' '}
              <code>/</code> (owner) or <code>:</code> boundaries. Branch restrictions go below, not in the subject.
            </p>
          </div>
          <AllowedRefsField value={allowedRefs} onChange={setAllowedRefs} />
        </div>

        {error && <div className="feedback-error" style={{ marginTop: 12 }}>{error}</div>}
      </form>
    </Modal>
  )
}
